import React, {useMemo} from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {Table} from 'reactstrap';
import {FormView} from '@src/components/_views/configurable/FormView';
import {IUser} from '@src/@interfaces/services/Auth/IUser';
import {FormAbstractFieldProps} from '@src/@types/components/Form';
import app from '@src/helpers/app';
import {withRouterApi} from '@src/HOCs/withRouterApi';
import {FormViewProps} from '@src/@types/components/FormViewProps';
import FormViewForm from '@src/components/_forms/FormViewForm';
import Translate from '@src/components/Translate/Translate';
import ListColumnsFactory from '@src/components/List/columns/ListColumnsFactory';
import {ListConfigFieldTypes} from '@src/@types/components/List';
import {WithRouterApiProps} from '@src/@types/hocs/WithRouterApiProps';
import {clearUserRequisites, fetchUserModel, setUserModel} from '@src/_redux/user/actions';
import {IReduxStoreState} from '@src/@interfaces';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import UAParser from 'ua-parser-js';
import RenderIf from '@src/components/RenderIf/RenderIf';

const baseTokensFieldConnector = connect(
        (state: IReduxStoreState) => ({
            user: state.user.model,
            userTokens: state.user.model?.tokens,
        }),
        {setUserModel, clearUserRequisites}
);

type BaseTokensFieldProps =
        WithRouterApiProps
        & ConnectedProps<typeof baseTokensFieldConnector>
        & FormAbstractFieldProps<IUser, string>;

function BaseTokensField(props: BaseTokensFieldProps) {
    const tokens = props.userTokens ?? [];
    const clearUserRequisites = props.clearUserRequisites;
    const [
        DateColumn,
        TimeSinceColumn,
        clearAllTokens,
    ] = useMemo(() => {
        const factory = new ListColumnsFactory();

        const clearAllTokens = () => {
            return app('http')
                    .route('profile.remove-all-tokens', {})
                    .then(() => {
                        clearUserRequisites();
                    });
        };

        return [
            factory.getComponent({name: '', type: ListConfigFieldTypes.DATETIME}),
            factory.getComponent({name: '', type: ListConfigFieldTypes.TIME_SINCE}),
            clearAllTokens,
        ];
    }, [clearUserRequisites]);

    const fakeListConfig = {title: '', columns: {}};
    const userAgentParser = new UAParser();

    return <Table size="sm" hover>
        <thead>
            <tr>
                <Translate message="UsersProfile.fields.tokens.name" tag="th"/>
                <Translate message="UsersProfile.fields.tokens.created_at" tag="th"/>
                <Translate message="UsersProfile.fields.tokens.last_used_at" tag="th"/>
                <td className="text-end">
                    <Translate
                            message="UsersProfile.remove-all"
                            tag={ConfirmableButton}
                            type="button"
                            size="sm"
                            color="danger"
                            onlyOnePopoverCanBeOpened={true}
                            groupIdentifier="user-profile-tokens"
                            onClick={clearAllTokens}
                    />
                </td>
            </tr>
        </thead>
        <tbody>
            {tokens.map((token: any, index: number) => {
                const isCurrentToken = (props.user?.current_access_token_id ?? 0) === token.id;

                const userAgentDetails = token.name !== 'main' ? userAgentParser.setUA(token.name) : null;
                const OS = userAgentDetails?.getOS();
                const device = userAgentDetails?.getDevice();
                const browser = userAgentDetails?.getBrowser();

                return <tr key={`user-${props.model?.id ?? 0}-token-${token.id}`}>
                    <td valign="middle">
                        {userAgentDetails ? (
                                <div className="row" title={token.name}>
                                    <RenderIf expression={device?.model}>
                                        <span className="col-auto">{device?.vendor} {device?.model}</span>
                                    </RenderIf>
                                    <RenderIf expression={OS}>
                                        <span className="col-auto">{OS?.name} {OS?.version}</span>
                                    </RenderIf>
                                    <RenderIf expression={browser}>
                                        <span className="col-auto">{browser?.name} {browser?.version}</span>
                                    </RenderIf>
                                </div>
                        ) : token.name}
                        {isCurrentToken ? (
                                <Translate
                                        message="UsersProfile.is-current-access-token"
                                        tag="div"
                                        className="badge bg-success text-bg-success mt-1"
                                />
                        ) : null}
                    </td>
                    <td valign="middle">
                        <DateColumn
                                listConfig={fakeListConfig}
                                config={{name: 'created_at'}}
                                model={token}
                                rowIndex={index}
                                cellIndex={0}
                        />
                    </td>
                    <td valign="middle">
                        <TimeSinceColumn
                                listConfig={fakeListConfig}
                                config={{name: 'last_used_at'}}
                                model={token}
                                rowIndex={index}
                                cellIndex={0}
                        />
                    </td>
                    <td className="text-end" valign="middle">
                        <Translate
                                message="UsersProfile.remove"
                                tag={ConfirmableButton}
                                type="button"
                                size="sm"
                                color={isCurrentToken ? 'danger' : 'warning'}
                                onlyOnePopoverCanBeOpened={true}
                                groupIdentifier="user-profile-tokens"
                                onClick={() => {
                                    const logoutUrl = isCurrentToken ?
                                            app('url').route('auth@logout') :
                                            null;

                                    if (isCurrentToken && logoutUrl) {
                                        props.urlNavigate(logoutUrl);

                                        return;
                                    }

                                    return app('http').route('profile.remove-token', {
                                        token_id: String(token.id),
                                    }).then(user => {
                                        props.setUserModel(user);
                                    });
                                }}
                        />
                    </td>
                </tr>;
            })}
        </tbody>
    </Table>;
}

const TokensField = withRouterApi<WithRouterApiProps & FormAbstractFieldProps<IUser, string>>(
        baseTokensFieldConnector(BaseTokensField)
);

const customFields = {
    tokens: {
        component: TokensField,
    },
};

class UsersProfileForm<Model extends IEmptyModel = IUser> extends FormViewForm<Model> {
    protected get customFields() {
        return customFields as any;
    }

    protected getOnSuccessBehavior() {
        app().dispatchAction(fetchUserModel());

        return super.getOnSuccessBehavior();
    }

    protected renderCancelButton(): React.ReactNode {
        return null;
    }

    protected renderSaveAndExitButton() {
        return null;
    }
}

export class UsersProfile extends FormView<IUser> {
    protected static pageTitle = 'UsersProfile.page-title';

    protected getUrlParams(): Readonly<Partial<{ id: string }>> {
        return {id: String(app('auth').getUser()?.id ?? 0)};
    }

    protected isUpdatePageContext(): boolean {
        return true;
    }

    protected getPageTitleFromConfig(): string | null {
        return null;
    }

    protected getFormComponent() {
        return UsersProfileForm;
    }
}

export default UsersProfile.getStoreConnector()(withRouterApi<FormViewProps>(UsersProfile));
